<template>
  <Page>
    <div
      v-if="hasPpmEnabled"
      class="custom-card card mb-4"
    >
      <div class="text-primary font-semibold">
        Payment Gateways
      </div>
      <div class="text-sm sm:max-w-[80%]">
        Easily let your customers pay in full or pay over time with your own payment plans, fully managed by Elective.
      </div>
      <div
        v-for="{ name, logoSrc, logoCss, buttonComponent, integrations, hasActiveIntegration, hidden } in availablePsp"
        :key="name"
        class="flex flex-col mt-6"
      >
        <div
          v-if="!hidden"
          class="w-full flex justify-between"
        >
          <div class="text-sm font-semibold text-primary">
            <img
              :src="logoSrc"
              :alt="`${name} Logo`"
              :class="logoCss"
              class="self-start mb-2"
            >
            {{ name }}
          </div>

          <component
            :is="buttonComponent"
            v-if="buttonComponent && !hasActiveIntegration"
            :projectId="projectId"
          />

          <div
            v-if="!buttonComponent && !hasActiveIntegration"
            class="self-end"
          >
            <button
              class="text-center btn btn-primary btn-sm"
              @click="router.push({name: `${name.toLowerCase()}Form`})"
            >
              Add Account
            </button>
          </div>
        </div>
        <CredentialsTable
          v-if="integrations && integrations.length"
          :integrations="integrations"
          @archive="loadIntegrations()"
        />
      </div>
    </div>

    <TaxGroup
      :integrations="taxIntegrations"
      @archive="loadIntegrations()"
    />

    <div class="custom-card card my-4">
      <div class="text-primary font-semibold">
        Workflow Automation
      </div>
      <div class="text-sm sm:max-w-[80%]">
        Build out seamless automations by connecting Elective to hundreds of different applications via Zapier.
      </div>
      <div class="flex justify-between items-center mt-4">
        <div class="text-sm font-semibold text-primary">
          <img
            src="./assets/zapier.svg"
            alt="Zapier Logo"
            class="h-[33px] self-start mb-2"
          >
          Zapier
        </div>
        <div class="self-end">
          <button
            class="text-center btn btn-primary btn-sm"
            @click="router.push({name: 'zapierDashboard'})"
          >
            Connect
          </button>
        </div>
      </div>
    </div>

    <!-- google tag manager -->
    <div
      v-if="isElectiveProject"
      class="custom-card card my-4"
    >
      <div class="text-primary font-semibold">
        Checkout Tracking
      </div>
      <div class="text-sm sm:max-w-[80%]">
        Track your customers' checkout process and see how they're progressing.
      </div>

      <div class="flex justify-between items-center mt-4">
        <div class="text-sm font-semibold text-primary">
          <img
            src="./assets/google_tag_manager.svg"
            alt="Google Tag Manager Logo"
            class="h-[33px] self-start mb-2"
          >
          Google Tag Manager
        </div>
        <div class="self-end">
          <button
            class="text-center btn btn-primary btn-sm"
            @click="router.push({name: 'googleTagManagerDashboard'})"
          >
            {{ gtmIntegration ? 'Edit' : 'Connect' }}
          </button>
        </div>
      </div>
    </div>
  </Page>
</template>

<script setup lang="ts">
import {
  ref,
  onBeforeMount,
  inject,
  Ref,
  computed,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Page from '@/pages/app/project/dashboard/components/page.vue';
import { ProjectState } from '@/pages/app/project/api/get_project_state';
import stripeLogo from './assets/stripe.svg';
import nmiLogo from './assets/nmi.svg';
import StripeButton from './stripe/index.vue';
import CredentialsTable from './components/credentials_table.vue';
import { getIntegrations, Integration } from './api';
import TaxGroup from './sales_tax/index.vue';

interface AvailablePsp {
  name: string;
  logoSrc: string;
  logoCss: string;
  buttonComponent?: any;
  integrations?: Integration[];
  hasActiveIntegration?: boolean;
  hidden: boolean;
}
const route = useRoute();
const router = useRouter();
const projectState = inject<Ref<ProjectState>>('project') as Ref<ProjectState>;
const projectId = ref<string>(route.params.projectId as string);
const hasPpmEnabled = projectState.value.features?.ppmEnabled;
const existingIntegrations = ref<Integration[]>([]);
const paymentIntegrations = ref<Integration[]>([]);
const gtmIntegration = ref<Integration | null>(null);
const taxIntegrations = ref<Integration[]>([]);
function getPspIntegration(name: string): Integration[] {
  return paymentIntegrations.value.filter((integration) => integration.integrationSource.toLowerCase() === name.toLowerCase());
}

const isElectiveProject = computed(() => {
  const environment = (import.meta.env.VITE_APP_ENVIRONMENT ?? '').toLowerCase();
  if (environment === 'production') {
    return [
      'e66510ac-6be6-441c-af8a-8eae083ec47f',
      '1c76fc74-8d89-423c-859c-3e7da280ce73',
      'eeafd5e0-8281-4b3b-bc6c-5542f431cf6e',
    ].includes(projectId.value);
  }

  return true;
});
const availablePsp = ref<AvailablePsp[]>([
  {
    name: 'Stripe',
    logoSrc: stripeLogo,
    logoCss: 'w-[74px] h-[31.34px]',
    buttonComponent: StripeButton,
    hidden: false,
  },
  {
    name: 'NMI',
    logoSrc: nmiLogo,
    logoCss: 'w-[74.13px] h-[23.88px]',
    hidden: false,
  },
]);

async function loadIntegrations() {
  existingIntegrations.value = await getIntegrations(projectId.value);
  taxIntegrations.value = existingIntegrations.value.filter((integration) => integration.integrationType === 'sales_tax');
  gtmIntegration.value = existingIntegrations.value.find((integration) => integration.integrationSource === 'google_tag_manager') ?? null;

  if (!hasPpmEnabled) return;
  paymentIntegrations.value = existingIntegrations.value.filter((integration) => integration.integrationType === 'payments');
  availablePsp.value.forEach((psp) => {
    psp.integrations = getPspIntegration(psp.name);
    psp.hasActiveIntegration = psp.integrations?.filter((i) => i.state !== 'unauthorized').length > 0;
  });
}

onBeforeMount(async () => {
  await loadIntegrations();
});

</script>

<style scoped lang="scss">
.custom-card {
  @apply p-4 bg-white max-w-lg;
}

</style>
